/**
 * Vincent P1.8.c (Eric 2026-07-31 SPEC STRICK PROD) — Extraction du hook
 * `useVideoData` depuis `VideoDetailPage.js`.
 *
 * Encapsule le fetch principal de la vidéo (`GET /api/videos/:id`) et ses
 * états dérivés (`data`, `loading`). Le hook expose `loadVideoData` comme
 * fonction stable (useCallback) pour être appelable depuis tous les handlers
 * (import DOCX, save, reprocess speakers, etc.) et depuis les refs internes
 * de `usePipelineStatus`.
 *
 * IMPORTANT — Ce hook fait UNIQUEMENT le fetch + setData. Les side-effects
 * qui étaient inline dans l'ancien `loadVideoData` (démarrage du polling
 * pipeline, track-view, pré-chargement des suggestions de parcours,
 * auto-seek `?t=`) restent dans `VideoDetailPage.js` sous forme d'effets
 * réactifs sur `data`. Motivation : isoler les responsabilités et permettre
 * des tests unitaires du fetcher sans mocker startPolling/isAdmin/etc.
 *
 * Reste dans VideoDetailPage :
 *   - `loadProcedureDoc`, `loadAllTranscripts`, `loadCategoriesAndPathways`
 *     (fetch complémentaires — extraction future dans un `useVideoAuxData`).
 *   - `useEffect` de montage qui appelle `loadVideoData()` + les autres.
 *   - Effets réactifs (voir plus bas).
 */
import { useState, useCallback, useEffect } from "react";
import axios from "axios";

export function useVideoData({ videoId, api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Vincent B797 (Eric 2026-02-12) — RÉINITIALISE `data` et `loading`
  // quand `videoId` change (navigation Précédent/Suivant). Sans ça, la
  // page affichait l'ancienne vidéo (durée, contenu vidéo, knowledge
  // units, transcript, etc.) jusqu'à ce que le nouveau fetch se
  // termine — donnant l'impression que « seulement le titre est
  // rafraîchi » (Vincent). En passant `data` à null et `loading` à
  // true, la page montre proprement l'état de chargement pendant
  // la transition puis affiche la nouvelle vidéo en entier.
  useEffect(() => {
    setData(null);
    setLoading(true);
  }, [videoId]);

  const loadVideoData = useCallback(async () => {
    try {
      const { data: videoData } = await axios.get(
        `${api}/api/videos/${videoId}`,
        { withCredentials: true },
      );
      setData(videoData);
    } catch (err) {
      console.error("[useVideoData] Failed to load video:", err);
    } finally {
      setLoading(false);
    }
  }, [videoId, api]);

  return { data, setData, loading, setLoading, loadVideoData };
}
